/**
 * Credit contracts — slice 6, "results & credits".
 *
 * The ledger is the highest-risk component in the product, and every shape in
 * this file exists to make one class of mistake structurally impossible rather
 * than to forbid it in prose.
 *
 * **CREDITS ARE WEND'S METERING UNIT, NOT A COST PASS-THROUGH.** Under BYO the
 * tenant's messages leave the tenant's own AWS account and — from slice 7 —
 * their own Meta WABA. AWS and Meta bill them directly, for amounts Wend never
 * sees and cannot compute. A credit measures how much of their *Wend plan* they
 * have used, and it has no exchange rate with anybody's invoice.
 *
 * So no field in this file, and no field in any contract that reads it, may be
 * called `cost`, `spend`, `charge` or `price`. That is not decoration: field
 * names become column headings, and a column headed "Spend" over a number that
 * is not money is a support ticket at best and a billing dispute at worst. The
 * prose here says **consume** and **release** for the same reason — the
 * vocabulary a file uses about itself is the vocabulary that reaches the UI.
 * Slice 7 makes the gap unarguable: Meta bills per 24-hour *conversation*, so
 * five WhatsApp messages inside one conversation are five credits here and one
 * line on the tenant's Meta invoice.
 *
 * **The ledger is append-only and is the source of truth. `credit_balances` is
 * a transactionally-maintained cache.** Every entry carries a unique
 * `idempotency_key`, so a replayed webhook hits the constraint and no-ops, and
 * the balance moves **only if a row was actually inserted**, in the same
 * transaction. {@link reserveKey}, {@link consumeKey} and {@link releaseKey} are
 * the only supported way to build one — CONTRACT.md fixes the format, and a
 * caller that concatenates strings is a caller that can produce
 * `reserve:undefined` and collapse every message onto one key.
 *
 * **`available` is `balance − reserved`, and it is a function.** There is no
 * third stored number, on the row or on the wire — see {@link availableCredits}
 * and the note on {@link creditBalanceSchema}.
 *
 * What is *not* here: the provider event vocabulary and the rule that turns an
 * event into one of these entries. That lives in `./delivery.js`, which imports
 * {@link CreditEffect} from this file and is the only thing that may. The split
 * is deliberate — `delivery.ts` parses an attacker-controllable payload, this
 * file parses nothing that came off a wire we do not own, and keeping them apart
 * means the single arrow between them is a named table a reviewer can read in
 * one sitting.
 */
import { z } from 'zod';

import {
  isoDateTimeSchema,
  paginated,
  paginationQuerySchema,
  uuidSchema,
} from '../common.js';
import { channelSchema, ledgerEntryTypeSchema, type Channel, type LedgerEntryType } from '../enums.js';

/* -------------------------------------------------------------------------- */
/* The unit                                                                    */
/* -------------------------------------------------------------------------- */

/**
 * One message, one credit.
 *
 * A constant rather than a literal `1` scattered through the send path, so the
 * places that reserve and the places that consume are visibly the same number.
 *
 * It is deliberately **not** per-channel. A WhatsApp message will consume one
 * credit exactly as an email does, and that will not line up with Meta's
 * per-conversation billing — which is correct, because a credit is Wend's
 * metering unit and not a share of anybody's invoice. If v2 ever wants a
 * channel weighting it becomes a lookup and the ledger shape does not change:
 * {@link creditLedgerEntrySchema} already carries an `amount` rather than
 * assuming one.
 */
export const CREDITS_PER_MESSAGE = 1;

/** Free-text on a ledger entry: who adjusted it, or which reconciliation ran. */
export const MAX_CREDIT_NOTE_LENGTH = 500;

/* -------------------------------------------------------------------------- */
/* Idempotency keys — the exactly-once guarantee                               */
/* -------------------------------------------------------------------------- */

/**
 * The three message-scoped key prefixes, exactly as CONTRACT.md fixes them:
 * `reserve:<message_id>`, `consume:<message_id>`, `release:<message_id>`.
 *
 * `grant`, `refund` and `adjustment` are absent because they are not scoped to a
 * message — an admin top-up (slice 8) has no message id to key on, and giving it
 * a made-up one here would put a key in `credit_ledger.idempotency_key` that
 * {@link parseCreditIdempotencyKey} would happily read back as belonging to a
 * message that does not exist. Slice 8 defines its own key shape; it must not be
 * `<type>:<uuid>`.
 *
 * It now exists: `topupIdempotencyKey` in `./admin.js` builds
 * `topup:<tenant_id>:<request_token>` — three segments, so
 * {@link parseCreditIdempotencyKey} rejects it twice over, on a prefix it does
 * not own and on an id half that is not a uuid. That file imports this one and
 * this one must never import it back.
 */
export const CREDIT_KEY_PREFIXES = {
  reserve: 'reserve',
  consume: 'consume',
  release: 'release',
} as const satisfies Readonly<Record<string, string>>;

/** The entry types that are keyed by message. A subset of `LedgerEntryType`. */
export type MessageScopedEntryType = Extract<LedgerEntryType, 'reserve' | 'consume' | 'release'>;

/**
 * Builds one of the three message-scoped keys, or throws.
 *
 * **Throwing is the point.** Every other "I cannot answer that" in this package
 * returns `null` — `dedupeKeyForContact`, `localHourInTimeZone` — because those
 * are data conditions a caller has to handle. This is not one: the message id
 * came out of `messages.id`, so a value that is not a uuid is a programming
 * error, and the two ways a caller could handle a `null` here are both
 * catastrophic. Writing the key anyway gives every affected message the same
 * key, so the unique constraint silently swallows every entry after the first.
 * Skipping the entry loses the credit movement without a trace. Neither shows up
 * until a reconciliation months later reports drift with nothing to attribute it
 * to.
 */
function messageScopedKey(type: MessageScopedEntryType, messageId: string): string {
  const parsed = uuidSchema.safeParse(messageId);
  if (!parsed.success) {
    throw new TypeError(
      `A ${type} credit key needs a message id and was given ${
        messageId === '' || messageId === undefined ? 'nothing' : 'a value that is not a uuid'
      }. This is a bug in the caller: the id comes from messages.id.`,
    );
  }
  return `${CREDIT_KEY_PREFIXES[type]}:${parsed.data}`;
}

/**
 * `reserve:<message_id>` — written **in the same transaction as the `messages`
 * row**, by the send path, before any provider event can exist.
 *
 * This is why the event→credit table in `./delivery.js` has no member that
 * produces a `reserve`: by the time an event arrives the reservation is already
 * there, and a second one would be the same key.
 */
export function reserveKey(messageId: string): string {
  return messageScopedKey('reserve', messageId);
}

/**
 * `consume:<message_id>` — written when a delivery event lands, and by nothing
 * else.
 *
 * **Replaying the same delivery webhook five times produces exactly one entry**
 * because the key is a function of the message and not of the event: five
 * different SNS `MessageId`s carrying the same delivery still compute this same
 * string, so four of the five inserts hit the unique constraint and no-op, and
 * the balance moves once because it moves only when a row was inserted.
 *
 * A consume presumes the reservation exists — it decrements `reserved` as well
 * as `balance`, and `credit_balances.reserved` carries `CHECK (>= 0)`. The write
 * path therefore ensures the reservation first, which {@link reserveKey} makes a
 * safe one-liner: re-reserving an already-reserved message is a no-op.
 */
export function consumeKey(messageId: string): string {
  return messageScopedKey('consume', messageId);
}

/**
 * `release:<message_id>` — the reservation is unwound and the credit returns to
 * `available` untouched.
 *
 * Not to be confused with `refund`, which the same word in English would cover
 * and which this key must never be used for. A release reverses a **reservation**
 * (nothing was consumed); a refund reverses a **consumption** (something was).
 * Treating one as the other double-counts: releasing a message whose delivery
 * already consumed drives `reserved` negative and the CHECK aborts a transaction
 * that should have been a no-op.
 */
export function releaseKey(messageId: string): string {
  return messageScopedKey('release', messageId);
}

export const creditIdempotencyKeySchema = z.string().trim().min(1).max(200);

/**
 * Reads a message-scoped key back apart. Returns `null` for anything else,
 * including a well-formed key of a type this scheme does not own.
 *
 * The reconciliation job uses it to attribute an entry without a join, and the
 * unit tests use it to prove the builders and the parser agree. It deliberately
 * refuses a key whose id half is not a uuid: a lenient parser here would make a
 * malformed key look attributable, which is precisely the state the builders
 * throw to prevent.
 */
export function parseCreditIdempotencyKey(
  key: string,
): { type: MessageScopedEntryType; messageId: string } | null {
  const separator = key.indexOf(':');
  if (separator < 0) return null;
  const prefix = key.slice(0, separator);
  const messageId = key.slice(separator + 1);
  if (prefix !== 'reserve' && prefix !== 'consume' && prefix !== 'release') return null;
  return uuidSchema.safeParse(messageId).success ? { type: prefix, messageId } : null;
}

/* -------------------------------------------------------------------------- */
/* What an entry does to a balance                                             */
/* -------------------------------------------------------------------------- */

/**
 * The two numbers a tenant actually has, per `(tenant_id, channel)`.
 *
 * There is no third one. See {@link availableCredits}.
 */
export interface CreditAmounts {
  /** Credits held. Only `grant`, `consume`, `refund` and `adjustment` move it. */
  readonly balance: number;
  /** Credits spoken for by messages in flight. Never negative — a CHECK holds it. */
  readonly reserved: number;
}

export const ZERO_CREDIT_AMOUNTS: CreditAmounts = { balance: 0, reserved: 0 };

/** How one entry type moves each number, per unit of `amount`. */
export interface CreditBalanceEffect {
  readonly balance: -1 | 0 | 1;
  readonly reserved: -1 | 0 | 1;
}

/**
 * **The whole credit model, as six rows.**
 *
 * Total over {@link LedgerEntryType}, so a seventh entry type cannot be added to
 * CONTRACT.md's fixed enum without this table failing to compile — which is the
 * only moment anybody would think about what it does to a balance.
 *
 * Read it down the `reserved` column and the lifecycle falls out:
 *
 * ```
 *                 balance   reserved     available (= balance − reserved)
 *   grant           +1         0            +1     a top-up is spendable at once
 *   reserve          0        +1            −1     spoken for the moment we send
 *   release          0        −1            +1     unwound; the credit comes back
 *   consume         −1        −1             0     ← the interesting one
 *   refund          +1         0            +1     a consumption reversed
 *   adjustment      +1*        0            +1*    * signed amount; see below
 * ```
 *
 * **`consume` leaves `available` unchanged, and that is correct.** A delivery
 * does not change how much a tenant can still send — the reservation already
 * did, at send time. All a consumption does is turn a number that was spoken for
 * into a number that is gone. Anyone reading a dashboard where the available
 * figure drops when deliveries land is looking at a bug.
 *
 * `adjustment` is **the only type whose amount may be negative**, because
 * CONTRACT.md fixes six members and a correction has to be able to go both ways.
 * {@link creditLedgerEntrySchema} refuses a negative amount on every other type:
 * a `consume` of −1 is a credit invented out of nothing, and it would balance.
 */
export const LEDGER_BALANCE_EFFECTS: Readonly<Record<LedgerEntryType, CreditBalanceEffect>> = {
  grant: { balance: 1, reserved: 0 },
  reserve: { balance: 0, reserved: 1 },
  release: { balance: 0, reserved: -1 },
  consume: { balance: -1, reserved: -1 },
  refund: { balance: 1, reserved: 0 },
  adjustment: { balance: 1, reserved: 0 },
};

/** The minimum of an entry that {@link applyLedgerEntry} needs. */
export interface LedgerMovement {
  readonly type: LedgerEntryType;
  readonly amount: number;
}

/**
 * Applies one entry to a pair of amounts. Pure; returns a new object.
 *
 * This is the same arithmetic `credit_balances` is maintained with, expressed
 * once so the nightly reconciliation and the transactional cache cannot be two
 * different opinions about what a `release` does. Criterion 6 — *the
 * reconciliation re-sums the ledger and reports zero drift* — is only meaningful
 * if the re-sum and the increment agree by construction rather than by review.
 *
 * It does **not** refuse a result below zero. That is
 * `credit_balances_balance_non_negative`'s job, in the same transaction as the
 * insert, where refusing actually stops something; clamping here would hide the
 * overdraw from the reconciliation that exists to find it.
 */
export function applyLedgerEntry(amounts: CreditAmounts, entry: LedgerMovement): CreditAmounts {
  const effect = LEDGER_BALANCE_EFFECTS[entry.type];
  return {
    balance: amounts.balance + effect.balance * entry.amount,
    reserved: amounts.reserved + effect.reserved * entry.amount,
  };
}

/**
 * Re-sums a run of ledger entries from zero — what reconciliation compares the
 * cached balance against.
 *
 * Fold order does not matter (addition commutes), which is what makes it safe to
 * run over a paged read of an append-only table.
 */
export function sumLedgerEntries(entries: readonly LedgerMovement[]): CreditAmounts {
  return entries.reduce<CreditAmounts>(applyLedgerEntry, ZERO_CREDIT_AMOUNTS);
}

/* -------------------------------------------------------------------------- */
/* The ledger entry                                                            */
/* -------------------------------------------------------------------------- */

/**
 * One row of `credit_ledger`, as the history screen reads it.
 *
 * **Append-only.** Nothing in this package expresses an update or a delete, and
 * migration 0011 grants the application role neither — the same shape
 * `suppressions` takes, for the same reason: a correction is an `adjustment`
 * entry that anybody can see, not an edit that nobody can.
 *
 * There is deliberately **no `balanceAfter`**. A stored running balance is a
 * second source of truth for the number reconciliation exists to check, and it
 * drifts on exactly the events that matter — a replayed webhook, a repair, a
 * backfill. The history screen renders the movement and the type; a running
 * total is {@link sumLedgerEntries} over the rows it already has.
 *
 * `messageId` and `campaignId` are nullable because a grant has neither.
 * `campaignId` is carried rather than joined so a campaign's credit totals are
 * one indexed aggregate — `messages` would otherwise have to be joined to a
 * partitioned table to answer "what did this campaign use".
 */
export const creditLedgerEntrySchema = z
  .object({
    id: uuidSchema,
    channel: channelSchema,
    type: ledgerEntryTypeSchema,
    /**
     * Credits moved, in units of {@link CREDITS_PER_MESSAGE}. Positive for every
     * type except `adjustment`, which may be negative — see
     * {@link LEDGER_BALANCE_EFFECTS}. Never zero: an entry that moves nothing is
     * a row that consumed an idempotency key and did not say anything.
     */
    amount: z.number().int(),
    /** The message this movement belongs to. Null for grants and adjustments. */
    messageId: uuidSchema.nullable(),
    /** Denormalised from the message, so campaign totals need no join. */
    campaignId: uuidSchema.nullable(),
    /** Unique. Built by {@link reserveKey} / {@link consumeKey} / {@link releaseKey}. */
    idempotencyKey: creditIdempotencyKeySchema,
    /** Why, in words, for the entries a person caused. Never a credential. */
    note: z.string().max(MAX_CREDIT_NOTE_LENGTH).nullable(),
    createdAt: isoDateTimeSchema,
  })
  .strict()
  .superRefine((entry, ctx) => {
    if (entry.amount === 0) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ['amount'],
        message: 'A ledger entry must move a non-zero number of credits',
      });
      return;
    }
    // The one rule that keeps the ledger from being able to invent credits: only
    // an adjustment is signed. A negative `consume` would add to the balance and
    // still re-sum correctly, so nothing downstream would ever notice.
    if (entry.amount < 0 && entry.type !== 'adjustment') {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ['amount'],
        message: `Only an adjustment may carry a negative amount; a ${entry.type} may not`,
      });
    }
  });
export type CreditLedgerEntry = z.infer<typeof creditLedgerEntrySchema>;

export const creditLedgerListQuerySchema = paginationQuerySchema.extend({
  channel: channelSchema.optional(),
  type: ledgerEntryTypeSchema.optional(),
  /** Everything this campaign moved — the credits half of the results screen. */
  campaignId: uuidSchema.optional(),
});
export type CreditLedgerListQuery = z.infer<typeof creditLedgerListQuerySchema>;

export const creditLedgerListResponseSchema = paginated(creditLedgerEntrySchema);
export type CreditLedgerListResponse = z.infer<typeof creditLedgerListResponseSchema>;

/* -------------------------------------------------------------------------- */
/* The balance                                                                 */
/* -------------------------------------------------------------------------- */

/**
 * A tenant's credits on one channel.
 *
 * **There is no `available` field, and there must never be one.** It is
 * `balance − reserved` and it is {@link availableCredits}. `.strict()` is what
 * enforces that: adding the field would make the API fail contract parsing on
 * its own response, rather than shipping a number that can disagree with the two
 * printed beside it. The same argument as `campaignReach` — one derivation, two
 * consumers — with the extra edge that here the derived number is the one a
 * customer makes a decision on.
 *
 * A tenant with no `credit_balances` row has **zero**, and the response says so
 * with a zero rather than by omitting the channel. A missing channel renders as
 * a blank where "0" belongs, which is the same bug `recipientStatusCounts` was
 * built to prevent one screen over.
 */
export const creditBalanceSchema = z
  .object({
    channel: channelSchema,
    /** Credits held. Moved by grants, consumptions, refunds and adjustments. */
    balance: z.number().int().min(0),
    /** Credits spoken for by messages in flight. `CHECK (>= 0)` in the database. */
    reserved: z.number().int().min(0),
    /** When the cache last moved. Null when the tenant has never had a credit. */
    updatedAt: isoDateTimeSchema.nullable(),
  })
  .strict();
export type CreditBalance = z.infer<typeof creditBalanceSchema>;

/**
 * **`available = balance − reserved`.** The only supported way to get it.
 *
 * `max(0, …)` guards the one case the CHECKs cannot: `balance` and `reserved`
 * are two columns, and a read that straddles a transaction which consumed
 * between them could momentarily produce a negative. A negative "credits left"
 * on a dashboard is read as a fault; zero is read as out of credits, which is
 * the true answer either way.
 */
export function availableCredits(amounts: CreditAmounts): number {
  return Math.max(0, amounts.balance - amounts.reserved);
}

/**
 * Whether `count` more messages can be reserved right now.
 *
 * Lives here, rather than in the sender, because two things ask it: the send
 * loop, which pauses the campaign at `paused_insufficient_credits` when the
 * answer turns false mid-run, and the campaign screen, which says so before
 * anybody presses Schedule. Two implementations of "has this tenant got enough"
 * drift, and the way they drift is that the screen keeps saying yes.
 *
 * SLICES: running out mid-campaign **pauses**. Remaining recipients stay
 * `pending` and it resumes on top-up — it does not fail, and it does not
 * silently keep sending.
 */
export function canReserveCredits(amounts: CreditAmounts, count: number): boolean {
  return count >= 0 && availableCredits(amounts) >= count * CREDITS_PER_MESSAGE;
}

/** Credits needed to send to `recipients` people. One place, one multiplication. */
export function creditsRequiredFor(recipients: number): number {
  return Math.max(0, recipients) * CREDITS_PER_MESSAGE;
}

export const creditBalancesResponseSchema = z.object({
  /** One entry per channel, always present, zeros included. */
  balances: z.array(creditBalanceSchema),
});
export type CreditBalancesResponse = z.infer<typeof creditBalancesResponseSchema>;

/** Zero balances for every channel — what a tenant with no rows reads as. */
export function zeroCreditBalances(channels: readonly Channel[]): CreditBalance[] {
  return channels.map((channel) => ({ channel, balance: 0, reserved: 0, updatedAt: null }));
}

/* -------------------------------------------------------------------------- */
/* Campaign totals — the credits half of the results screen                    */
/* -------------------------------------------------------------------------- */

/**
 * What one campaign moved, counted from the ledger.
 *
 * Three counts rather than one net figure, because the net figure hides the
 * question the screen is being asked. `reserved − consumed − released` is how
 * many of this campaign's messages are still in flight, and it should equal the
 * number of messages sitting at `sent` with no terminal event — see
 * `campaignResultsReading` in `./delivery.js`, which surfaces both so a
 * disagreement between them is visible rather than averaged away.
 */
export const campaignCreditTotalsSchema = z
  .object({
    /** Credits reserved at send. One per message that was handed to the provider. */
    reserved: z.number().int().min(0),
    /** Credits consumed by delivery. **The only thing that consumes.** */
    consumed: z.number().int().min(0),
    /** Reservations unwound — bounced, rejected, never delivered. */
    released: z.number().int().min(0),
  })
  .strict();
export type CampaignCreditTotals = z.infer<typeof campaignCreditTotalsSchema>;

export const EMPTY_CAMPAIGN_CREDIT_TOTALS: CampaignCreditTotals = {
  reserved: 0,
  consumed: 0,
  released: 0,
};

/**
 * Turns a `GROUP BY type` over `credit_ledger` into the totals.
 *
 * Starts from zeros for the same reason `recipientStatusCounts` does: a grouped
 * aggregate returns no row for a type nothing produced, so a campaign that
 * bounced nothing would leave `released` undefined and render a blank where "0"
 * belongs. Entry types outside the three are dropped — a grant is not something
 * a campaign did.
 */
export function campaignCreditTotals(
  rows: readonly { readonly type: string; readonly total: number }[],
): CampaignCreditTotals {
  const totals = { ...EMPTY_CAMPAIGN_CREDIT_TOTALS };
  for (const row of rows) {
    if (row.type === 'reserve') totals.reserved = row.total;
    else if (row.type === 'consume') totals.consumed = row.total;
    else if (row.type === 'release') totals.released = row.total;
  }
  return totals;
}

/** Reservations this campaign still holds: reserved, minus everything resolved. */
export function creditsStillReserved(totals: CampaignCreditTotals): number {
  return totals.reserved - totals.consumed - totals.released;
}

/* -------------------------------------------------------------------------- */
/* Reconciliation — the nightly re-sum                                         */
/* -------------------------------------------------------------------------- */

/**
 * The ledger re-summed, beside the cache it is supposed to equal.
 *
 * SLICES: *a nightly reconciliation re-sums the ledger and alarms on drift*, and
 * criterion 6 is that it reports zero. The shape lives here rather than in the
 * job so the number the job alarms on and the number an admin screen shows are
 * produced by one function — a reconciliation whose two readers disagree is a
 * reconciliation that will be explained away rather than fixed.
 *
 * Drift is **cache minus ledger**, so the sign says which way the cache is
 * wrong: positive means the cache thinks the tenant has more than the ledger can
 * account for. The ledger is the source of truth, so drift is never repaired by
 * writing to the ledger — the cache is rebuilt from it.
 */
export const creditReconciliationSchema = z
  .object({
    channel: channelSchema,
    ledgerBalance: z.number().int(),
    ledgerReserved: z.number().int(),
    cachedBalance: z.number().int(),
    cachedReserved: z.number().int(),
    /** `cachedBalance − ledgerBalance`. Zero is the only acceptable answer. */
    balanceDrift: z.number().int(),
    /** `cachedReserved − ledgerReserved`. Zero is the only acceptable answer. */
    reservedDrift: z.number().int(),
    inSync: z.boolean(),
  })
  .strict();
export type CreditReconciliation = z.infer<typeof creditReconciliationSchema>;

export function creditReconciliation(
  channel: Channel,
  ledger: CreditAmounts,
  cached: CreditAmounts,
): CreditReconciliation {
  const balanceDrift = cached.balance - ledger.balance;
  const reservedDrift = cached.reserved - ledger.reserved;
  return {
    channel,
    ledgerBalance: ledger.balance,
    ledgerReserved: ledger.reserved,
    cachedBalance: cached.balance,
    cachedReserved: cached.reserved,
    balanceDrift,
    reservedDrift,
    inSync: balanceDrift === 0 && reservedDrift === 0,
  };
}

export const creditReconciliationResponseSchema = z.object({
  checkedAt: isoDateTimeSchema,
  results: z.array(creditReconciliationSchema),
});
export type CreditReconciliationResponse = z.infer<typeof creditReconciliationResponseSchema>;

/* -------------------------------------------------------------------------- */
/* The effect an ingested event may have                                       */
/* -------------------------------------------------------------------------- */

/**
 * The ledger entry a provider event causes, or `null` for none.
 *
 * Typed as an `Extract` of {@link LedgerEntryType} rather than as its own
 * vocabulary, on purpose: the mapping table in `./delivery.js` is then written
 * in the ledger's own words, so the thing that decides and the thing that writes
 * cannot be describing different operations. A member spelled `'released'` there
 * would not compile here.
 *
 * **`reserve` is deliberately not in the union.** No event can cause one: the
 * reservation is written by the send path in the same transaction as the
 * `messages` row, before any event exists. A member nothing can produce is a
 * member nobody keeps accurate — the same reason `no_phone_number` is absent
 * from slice 5's skip reasons.
 */
export type CreditEffect = Extract<LedgerEntryType, 'consume' | 'release'> | null;

/** For the rare shape that carries a decision on the wire. `null` ⇒ absent. */
export const creditEffectSchema = z.enum(['consume', 'release']).nullable();
